// Card Product Variant Switching - TSS-012
// Handles variant swatch interactions on collection product cards

const STORAGE_KEY = 'tss012_variant_selections';
const DISPLAYED_GROUPS_KEY = 'tss012_displayed_groups';

function isTss012Active() {
  return document.body.classList.contains('TSS-012');
}

function shouldBypassGroupHiding() {
  return (
    Boolean(document.querySelector('[data-ab-tag-filter-component]')) &&
    !isTss012Active()
  );
}

function resetAllCardVisibility() {
  const cards = document.querySelectorAll('.card-product-tss-012');
  cards.forEach((card) => {
    const gridItem = card.closest('li.grid__item');
    card.style.display = '';
    if (gridItem) {
      gridItem.style.display = '';
    }
  });
}

/**
 * Get saved variant selections from localStorage
 */
function getSavedSelections() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (e) {
    console.error('TSS-012: Error reading localStorage', e);
    return {};
  }
}

/**
 * Save variant selection to localStorage
 */
function saveSelection(groupId, variantId) {
  try {
    const selections = getSavedSelections();
    selections[groupId] = variantId;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selections));
  } catch (e) {
    console.error('TSS-012: Error saving to localStorage', e);
  }
}

/**
 * Get currently displayed groups from localStorage
 * This tracks which groups are visible across all pages
 */
function getDisplayedGroups() {
  try {
    const saved = localStorage.getItem(DISPLAYED_GROUPS_KEY);
    const parsed = saved ? JSON.parse(saved) : {};
    console.log('TSS-012: Retrieved displayed groups from localStorage:', parsed);
    return parsed;
  } catch (e) {
    console.error('TSS-012: Error reading displayed groups', e);
    return {};
  }
}

/**
 * Save which card is displaying each group
 */
function saveDisplayedGroup(groupId, productId) {
  try {
    const displayed = getDisplayedGroups();
    displayed[groupId] = productId;
    localStorage.setItem(DISPLAYED_GROUPS_KEY, JSON.stringify(displayed));
    console.log(`TSS-012: Saved displayed group ${groupId} -> product ${productId}`);
  } catch (e) {
    console.error('TSS-012: Error saving displayed group', e);
  }
}

/**
 * Clear displayed groups tracking (called when collection/filter changes)
 */
function clearDisplayedGroups() {
  try {
    localStorage.removeItem(DISPLAYED_GROUPS_KEY);
    localStorage.removeItem(STORAGE_KEY); // Also clear variant selections on filter change
    console.log('TSS-012: Cleared displayed groups and variant selections');
  } catch (e) {
    console.error('TSS-012: Error clearing localStorage', e);
  }
}

// Track if already initialized to prevent duplicate initialization
let isInitialized = false;

/**
 * Initialize all card product variant swatches
 */
function initCardProducts() {
  if (isInitialized) {
    console.log('TSS-012 Cards: Already initialized, skipping');
    return;
  }

  console.log('TSS-012 Cards: Initializing');

  const cards = document.querySelectorAll('.card-product-tss-012');
  if (!cards.length) {
    console.log('TSS-012 Cards: No cards found');
    return;
  }

  console.log('TSS-012 Cards: Found', cards.length, 'cards');
  
  isInitialized = true;

  // On collection pages with AJAX tag filter, do not hide duplicate group cards.
  // We still initialize each card so swatches continue to work.
  if (shouldBypassGroupHiding()) {
    console.log('TSS-012 Cards: AJAX collection filter detected, bypassing group-based card hiding');
    cards.forEach((card) => {
      const gridItem = card.closest('li.grid__item');
      if (gridItem) {
        gridItem.style.display = '';
      }
      card.style.display = '';
      initializeCard(card, [card], null);
    });
    return;
  }

  if (!isTss012Active()) {
    console.log('TSS-012 Cards: Body class TSS-012 not found, skipping card grouping logic');
    return;
  }

  // Group cards by their group ID to ensure consistency across sorting/filtering
  const cardGroups = new Map();
  cards.forEach(card => {
    const groupId = card.dataset.groupId;
    const productId = card.dataset.productId;
    console.log(`TSS-012: Found card - Product ID: ${productId}, Group ID: ${groupId}`);
    if (groupId) {
      if (!cardGroups.has(groupId)) {
        cardGroups.set(groupId, []);
      }
      cardGroups.get(groupId).push(card);
    }
  });

  console.log('TSS-012 Cards: Found', cardGroups.size, 'unique variant groups');
  console.log('TSS-012 Cards: Group details:');
  cardGroups.forEach((groupCardList, groupId) => {
    const productIds = groupCardList.map(c => c.dataset.productId);
    console.log(`  Group ${groupId}: ${groupCardList.length} cards - Products [${productIds.join(', ')}]`);
  });

  // Get saved selections and displayed groups from localStorage
  const savedSelections = getSavedSelections();
  const displayedGroups = getDisplayedGroups();

  // For each group, determine which card should be visible
  cardGroups.forEach((groupCardList, groupId) => {
    console.log(`\nTSS-012: Processing group ${groupId}`);
    
    if (!groupId || groupId === 'undefined') {
      // No group ID, show the card normally
      console.log(`  No valid group ID, showing all ${groupCardList.length} cards normally`);
      groupCardList.forEach(card => {
        const gridItem = card.closest('li.grid__item');
        if (gridItem) {
          gridItem.style.display = '';
        }
        card.style.display = '';
        initializeCard(card, [card], null);
      });
      return;
    }

    // The group ID IS the first product's ID from the metaobject
    // ONLY show cards that match this first product ID
    const firstProductId = groupId;
    console.log(`  First product in metaobject order: ${firstProductId}`);
    
    // Check if this group was already displayed on a previous page
    const displayedProductId = displayedGroups[groupId];
    console.log(`  Previously displayed product for this group: ${displayedProductId || 'none'}`);
    
    // If group was displayed on a previous page and the displayed card is NOT on current page, hide all
    if (displayedProductId && !groupCardList.find(c => c.dataset.productId === displayedProductId)) {
      console.log(`  ✗ Group already displayed elsewhere - HIDING entire group on this page`);
      groupCardList.forEach(card => {
        const gridItem = card.closest('li.grid__item');
        console.log(`  → HIDING card for product ${card.dataset.productId} (group already displayed)`);
        if (gridItem) {
          gridItem.style.display = 'none';
        } else {
          card.style.display = 'none';
        }
      });
      return;
    }
    
    // Find the first product card - this is the ONLY one we'll show
    let primaryCard = groupCardList.find(c => c.dataset.productId === firstProductId);
    
    if (!primaryCard) {
      // First product is not on this page - hide the entire group
      console.log(`  ✗ First product ${firstProductId} not on this page - HIDING entire group`);
      groupCardList.forEach(card => {
        const gridItem = card.closest('li.grid__item');
        console.log(`  → HIDING card for product ${card.dataset.productId} (not the first product)`);
        if (gridItem) {
          gridItem.style.display = 'none';
        } else {
          card.style.display = 'none';
        }
      });
      return;
    }
    
    console.log(`  ✓ Found first product card on this page`);
    
    // Validate if primary card matches first variant in its data
    const primaryCardDataScript = primaryCard.querySelector('.card-product-tss-012__data');
    if (primaryCardDataScript) {
      try {
        const primaryCardData = JSON.parse(primaryCardDataScript.textContent);
        const firstVariantInData = primaryCardData.variants[0]?.id;
        const primaryCardProductId = parseInt(primaryCard.dataset.productId);
        
        console.log(`  Checking: Primary card product ID ${primaryCardProductId} vs first variant ID ${firstVariantInData}`);
        
        // If primary card does NOT match first variant, metaobject is incorrect - hide entire group
        if (primaryCardProductId !== firstVariantInData) {
          console.log(`  ✗ Primary card does NOT match first variant - Metaobject incorrect - HIDING entire group`);
          groupCardList.forEach(card => {
            const gridItem = card.closest('li.grid__item');
            console.log(`  → HIDING card for product ${card.dataset.productId} (metaobject mismatch)`);
            if (gridItem) {
              gridItem.style.display = 'none';
            } else {
              card.style.display = 'none';
            }
          });
          return;
        }
        
        console.log(`  ✓ Primary card MATCHES first variant - Metaobject correct - continuing with normal logic`);
      } catch (e) {
        console.error(`  Error parsing variant data:`, e);
      }
    }
    
    const savedVariantId = savedSelections[groupId];

    // Show only the first product card, hide all others
    groupCardList.forEach(card => {
      const gridItem = card.closest('li.grid__item');
      if (card === primaryCard) {
        console.log(`  → SHOWING card for product ${card.dataset.productId} (first in metaobject)`);
        if (gridItem) {
          gridItem.style.display = '';
        }
        card.style.display = '';
        saveDisplayedGroup(groupId, card.dataset.productId);
        initializeCard(card, groupCardList, savedVariantId);
      } else {
        console.log(`  → HIDING card for product ${card.dataset.productId} (not first in metaobject)`);
        // Hide the parent grid item to prevent gaps
        if (gridItem) {
          gridItem.style.display = 'none';
        } else {
          card.style.display = 'none';
        }
      }
    });
  });
}

function initializeCard(card, groupCards = [card], savedVariantId = null) {
  const swatches = card.querySelectorAll('.card-product-tss-012__variant-swatch');
  const mainImage = card.querySelector('.card-product-tss-012__main-image');
  const priceContainer = card.querySelector('.card-product-tss-012__price');
  const badges = card.querySelectorAll('.card-product-tss-012__badge');
  const links = card.querySelectorAll('.card-product-tss-012__link');
  const titleElements = card.querySelectorAll('.card-product-tss-012__title');
  const dataScript = card.querySelector('.card-product-tss-012__data');

  if (!swatches.length || !dataScript) {
    return;
  }

  // Store group cards reference for potential cross-card updates
  card._groupCards = groupCards;
  const groupId = card.dataset.groupId;

  // Parse variant data
  let variantData;
  try {
    variantData = JSON.parse(dataScript.textContent);
  } catch (e) {
    console.error('TSS-012: Failed to parse variant data', e);
    return;
  }

  // Apply saved selection if it exists and is different from current product
  if (savedVariantId && savedVariantId !== card.dataset.productId) {
    const savedVariant = variantData.variants.find(v => v.id === parseInt(savedVariantId));
    if (savedVariant && savedVariant.available) {
      // Update card to show saved variant
      updateCardImage(mainImage, savedVariant);
      updateCardPrice(priceContainer, savedVariant);
      updateCardBadges(badges, savedVariant);
      updateCardLinks(links, savedVariant);
      updateCardTitle(titleElements, savedVariant);
      
      // Update swatch selection state
      swatches.forEach(s => {
        if (s.dataset.variantProductId === savedVariantId) {
          s.classList.add('card-product-tss-012__variant-swatch--selected');
          s.setAttribute('aria-pressed', 'true');
        } else {
          s.classList.remove('card-product-tss-012__variant-swatch--selected');
          s.setAttribute('aria-pressed', 'false');
        }
      });
    }
  }

  // Remove any existing event listeners by cloning and replacing
  // (prevents duplicate handlers after filter updates)
  const swatchesArray = Array.from(swatches);
  swatchesArray.forEach(oldSwatch => {
    const newSwatch = oldSwatch.cloneNode(true);
    oldSwatch.parentNode.replaceChild(newSwatch, oldSwatch);
  });

  // Attach click handlers to swatches (now on fresh nodes)
  const freshSwatches = card.querySelectorAll('.card-product-tss-012__variant-swatch');
  freshSwatches.forEach(swatch => {
    swatch.addEventListener('click', (e) => {
      e.preventDefault();
      
      // Don't switch if already selected or sold out
      if (swatch.classList.contains('card-product-tss-012__variant-swatch--selected') || swatch.disabled) {
        return;
      }

      const variantId = swatch.dataset.variantProductId;
      
      console.log(`TSS-012: Swatch clicked - Variant ID: ${variantId}, Group ID: ${groupId}`);
      
      // Save selection to localStorage (tracks which variant is selected within the group)
      if (groupId) {
        saveSelection(groupId, variantId);
        // Note: We do NOT update displayedGroup here - that only tracks which card/page shows the group
      }

      // Find variant data in the variants array
      const selectedVariant = variantData.variants.find(v => v.id === parseInt(variantId));

      if (!selectedVariant) {
        console.error('TSS-012: Variant data not found for ID', variantId);
        return;
      }

      // Update selected state (use fresh swatches reference)
      freshSwatches.forEach(s => {
        s.classList.remove('card-product-tss-012__variant-swatch--selected');
        s.setAttribute('aria-pressed', 'false');
      });
      swatch.classList.add('card-product-tss-012__variant-swatch--selected');
      swatch.setAttribute('aria-pressed', 'true');

      // Update card content
      updateCardImage(mainImage, selectedVariant);
      updateCardPrice(priceContainer, selectedVariant);
      updateCardBadges(badges, selectedVariant);
      updateCardLinks(links, selectedVariant);
      updateCardTitle(titleElements, selectedVariant);

      // Announce change for screen readers
      announceVariantChange(selectedVariant);
    });
  });
}

function updateCardImage(imgElement, variant) {
  if (!imgElement || !variant.image) {
    return;
  }

  // Generate responsive srcset
  const imageSizes = [165, 360, 533, 720, 940, 1066];
  const srcset = imageSizes.map(size => `${variant.image.replace(/width=\d+/, `width=${size}`)} ${size}w`).join(', ');

  imgElement.srcset = srcset;
  imgElement.src = variant.image;
  imgElement.alt = variant.title;
}

function updateCardPrice(priceContainer, variant) {
  if (!priceContainer) {
    return;
  }

  const priceWrapper = priceContainer.querySelector('.price');
  const regularDiv = priceContainer.querySelector('.price__regular');
  const saleDiv = priceContainer.querySelector('.price__sale');

  if (!priceWrapper) {
    return;
  }

  // Check if product is on sale
  const isOnSale = variant.compareAtPrice && variant.compareAtPrice > variant.price;
  
  // Prepare price text with "From" prefix if product has varying prices
  const priceText = variant.priceVaries ? `From ${variant.priceFormatted}` : variant.priceFormatted;

  if (isOnSale) {
    // Show sale price structure
    priceWrapper.classList.add('price--on-sale');
    
    // Update compare at price (strikethrough) in sale div
    const saleComparePrice = saleDiv?.querySelector('.price-item--regular');
    if (saleComparePrice) {
      saleComparePrice.textContent = variant.compareAtPriceFormatted;
    }
    
    // Update sale price in sale div
    const salePriceElement = saleDiv?.querySelector('.price-item--sale');
    if (salePriceElement) {
      salePriceElement.textContent = priceText;
    }
  } else {
    // Show regular price structure
    priceWrapper.classList.remove('price--on-sale');
    
    // Update regular price in regular div
    const regularPriceElement = regularDiv?.querySelector('.price-item--regular');
    if (regularPriceElement) {
      regularPriceElement.textContent = priceText;
    }
  }
}

function updateCardBadges(badges, variant) {
  if (!badges.length) {
    return;
  }

  badges.forEach(badgeContainer => {
    const soldOutBadge = badgeContainer.querySelector('.color-' + (window.badgeSchemes?.soldOut || 'inverse'));
    const saleBadge = badgeContainer.querySelector('.color-' + (window.badgeSchemes?.sale || 'accent-2'));

    // Show/hide sold out badge
    if (soldOutBadge) {
      if (!variant.available) {
        soldOutBadge.style.display = '';
      } else {
        soldOutBadge.style.display = 'none';
      }
    }

    // Show/hide sale badge
    if (saleBadge) {
      if (variant.available && variant.compareAtPrice && variant.compareAtPrice > variant.price) {
        saleBadge.style.display = '';
      } else {
        saleBadge.style.display = 'none';
      }
    }
  });
}

function updateCardLinks(links, variant) {
  if (!links.length) {
    return;
  }

  links.forEach(link => {
    link.href = variant.url;
  });
}

function updateCardTitle(titleElements, variant) {
  if (!titleElements.length) {
    console.log('TSS-012: No title elements found');
    return;
  }

  console.log('TSS-012: Updating', titleElements.length, 'title elements to:', variant.title);
  
  titleElements.forEach(titleElement => {
    const link = titleElement.querySelector('a');
    if (link) {
      link.textContent = variant.title;
    }
  });
}

function formatMoney(cents) {
  // Fallback money formatting if priceFormatted is not available
  // The actual formatting is done server-side in Liquid
  const dollars = (cents / 100).toFixed(2);
  return `$${dollars}`;
}

function announceVariantChange(variant) {
  // Create or update live region for screen reader announcements
  let liveRegion = document.getElementById('tss-012-live-region');
  
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.id = 'tss-012-live-region';
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    document.body.appendChild(liveRegion);
  }

  const availabilityText = variant.available ? 'In stock' : 'Sold out';
  liveRegion.textContent = `${variant.title} selected. ${variant.priceFormatted}. ${availabilityText}.`;
}

/**
 * Wait for Intelligems to add TSS-012 class to body
 */
function waitForIntelligems(retryCount = 0) {
  const maxRetries = 3;
  const timeoutDuration = 10000; // 10 seconds
  
  console.log('TSS-012 Cards: Waiting for Intelligems to add TSS-012 class (attempt ' + (retryCount + 1) + ')');

  // Check if class already exists
  if (document.body.classList.contains('TSS-012')) {
    console.log('TSS-012 Cards: TSS-012 class found immediately');
    initCardProducts();
    return;
  }

  // Set up MutationObserver to watch for class changes
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        if (document.body.classList.contains('TSS-012')) {
          console.log('TSS-012 Cards: TSS-012 class detected via MutationObserver');
          observer.disconnect();
          initCardProducts();
        }
      }
    });
  });

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class']
  });

  // Fallback timeout - check one more time then optionally retry
  setTimeout(() => {
    observer.disconnect();
    
    // Final check after timeout
    if (document.body.classList.contains('TSS-012')) {
      console.log('TSS-012 Cards: TSS-012 class found after timeout');
      initCardProducts();
    } else if (retryCount < maxRetries) {
      console.log('TSS-012 Cards: Timeout reached, retrying...');
      waitForIntelligems(retryCount + 1);
    } else {
      console.warn('TSS-012 Cards: Max retries reached, TSS-012 class not found - initialization skipped');
    }
  }, timeoutDuration);
}

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', waitForIntelligems);
} else {
  waitForIntelligems();
}

// Handle page restoration from bfcache (back/forward navigation)
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    // Page restored from bfcache - reset and re-initialize cards
    console.log('TSS-012 Cards: Page restored from bfcache, re-initializing');
    isInitialized = false;
    waitForIntelligems();
  }
});

// Re-initialize cards when AJAX filter updates the product grid
document.addEventListener('ab-tag-filter:updated', () => {
  if (shouldBypassGroupHiding()) {
    console.log('TSS-012 Cards: Filter updated on AJAX collection page; re-initializing without group hiding');
    clearDisplayedGroups();
    isInitialized = false;
    resetAllCardVisibility();
    initCardProducts();
    return;
  }

  if (!isTss012Active()) {
    console.log('TSS-012 Cards: Filter updated, but TSS-012 is not active; keeping all products visible');
    isInitialized = false;
    resetAllCardVisibility();
    return;
  }

  console.log('TSS-012 Cards: Filter updated, clearing displayed groups and re-initializing cards');
  // Clear displayed groups when filter changes (new collection view)
  clearDisplayedGroups();
  isInitialized = false;
  initCardProducts();
});

// Watch for FacetFiltersForm updates (standard Shopify facets)
// Since facets.js doesn't dispatch events, we use MutationObserver
const productGridContainer = document.getElementById('ProductGridContainer');
if (productGridContainer) {
  const gridObserver = new MutationObserver((mutations) => {
    // Check if the mutation affected the product grid
    const hasProductGridChanges = mutations.some(mutation => {
      return mutation.type === 'childList' && mutation.target.id === 'ProductGridContainer';
    });
    
    if (hasProductGridChanges) {
      if (shouldBypassGroupHiding()) {
        console.log('TSS-012 Cards: Facet update on AJAX collection page; re-initializing without group hiding');
        clearDisplayedGroups();
        isInitialized = false;
        resetAllCardVisibility();
        setTimeout(() => {
          initCardProducts();
        }, 100);
        return;
      }

      if (!isTss012Active()) {
        console.log('TSS-012 Cards: Facet update detected, but TSS-012 is not active; keeping all products visible');
        isInitialized = false;
        resetAllCardVisibility();
        return;
      }

      console.log('TSS-012 Cards: Product grid updated via facets, re-initializing cards');
      // Clear displayed groups when filter changes
      clearDisplayedGroups();
      isInitialized = false;
      // Small delay to ensure DOM is fully updated
      setTimeout(() => {
        initCardProducts();
      }, 100);
    }
  });
  
  gridObserver.observe(productGridContainer, {
    childList: true,
    subtree: false
  });
  
  console.log('TSS-012 Cards: Watching ProductGridContainer for facet updates');
}
